<template>
    <div id="JoinableAgent">
        <div class="search-input">
            <a-input-search :placeholder="$t('auto.searchAgent')" v-model="searchText" @search="onSearch" />
        </div>
        <template v-if="filteredAgents.length > 0">
            <SupsisTable :columns="columns" :rows="filteredAgents" v-bind="tableOptions" />
        </template>
        <template v-else>
            <div class="no-agents-placeholder">
                <a-icon type="frown" />
                <p>{{ $t("auto.noAgentsAvailableToForward") }}</p>
            </div>
        </template>
    </div>
</template>
<script>
import SupsisTable from "../../../../components/SupsisTable.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { mapGetters, mapState } from "vuex";
import { sendSocketMessage } from "../../../../utils/socketStream";
import { log2 } from "../../../../utils/log2";

const log = log2("AgentList");
export default {
    name: "JoinableAgent",
    components: { SupsisTable, SupsisButton },
    props: {
        contact: {
            type: Object,
            default: () => {},
        },
        defaultContacts: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            columns: [
                {
                    title: this.$t("auto.joinableAgentName"),
                    key: "name",
                    field: "name",
                },
                {
                    title: this.$t("auto.status"),
                    key: "status",
                    field: "status",
                },
                {
                    title: this.$t("auto.departments"),
                    key: "subjects",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "div",
                            {},
                            this.getUserDepartmentNames(row).map((departmentName) => {
                                return h(
                                    "a-tag",
                                    {
                                        props: {
                                            color: this.getDepartmentColor(departmentName),
                                        },
                                        style: {
                                            marginRight: "4px",
                                        },
                                    },
                                    departmentName
                                );
                            })
                        );
                    },
                },
                {
                    title: this.$t("auto.lastLoginPlatformColumn"),
                    key: "lastLoginPlatform",
                    field: "lastLoginPlatform",
                },
                {
                    title: this.$t("auto.actions"),
                    key: "actions",
                    width: "100px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <b-button size="sm" on-click={() => this.assignAgent(row._id)}>
                                {this.$t("message.addChat")}
                            </b-button>
                        );
                    },
                },
            ],
            searchText: "",
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
                pagination: {
                    page: 1,
                    perPage: 10,
                    total: 0,
                    defaultPageSize: 10,
                    pageSizeOptions: [10, 20, 50],
                },
            },
            departmentColors: {},
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            departments: (state) => state.subject.subjects?.data || [],
            site: (state) => state.site.site,
            activeConversation: (state) => state.chat.activeContact?.lastConversation,
        }),
        ...mapGetters("channel", ["isChannelVisibleForMe", "isChannelVisibleForUser"]),
        availableAgents() {
            const conversation = this.contact.lastConversation;
            const subject = conversation?.subject;
            const channel = conversation?.channel;
            const departmentForceEnabled = !!this.settings.subject;
            const loggedUser = this.$store.state.auth.user;
            return this.users.filter((user) => {
                const isUserOnline = user.isOnline && user.status === "Online";
                const isMe = user._id === loggedUser._id;
                const subjectMatched =
                    !subject ||
                    !departmentForceEnabled ||
                    (departmentForceEnabled &&
                        (user.subjects?.includes(subject) || user.subjects?.length === 0 || user.subjects === null));
                const channelMatched = !channel || this.isChannelVisibleForUser(channel, user);
                const isRootUser = user.role.isRoot;
                const ownerMatched = isRootUser || !this.contact?.owner || this.contact?.owner === user._id;
                const isValid = isUserOnline && !isMe && subjectMatched && channelMatched && ownerMatched;
                if (!isValid) {
                    console.log("agent not valid to forward " + user.name, {
                        isUserOnline,
                        isMe,
                        subjectMatched,
                        channelMatched,
                        ownerMatched,
                    });
                }
                return isValid;
            });
        },
        filteredAgents() {
            if (!this.searchText) return this.availableAgents;
            const searchText = this.searchText.toLowerCase();
            return this.availableAgents.filter((agent) => agent.name.toLowerCase().includes(searchText));
        },
    },
    methods: {
        onSearch() {
            this.tableOptions.pagination.page = 1;
        },
        getUserDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.departments
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map((x) => {
                          const currentLocaleExists = !!x.languageContent[this.$i18n.locale];
                          const firstLocale = Object.keys(x.languageContent)[0];
                          return currentLocaleExists
                              ? x.languageContent[this.$i18n.locale].name
                              : x.languageContent[firstLocale].name;
                      })
                : [this.$t("message.allDepartments")];
        },
        getDepartmentColor(department) {
            if (!this.departmentColors[department]) {
                const colors = ["#B0E0E6", "#98FB98", "#FFDAB9", "#E0FFFF", "#FFE4E1"];
                const index = Object.keys(this.departmentColors).length % colors.length;
                this.departmentColors[department] = colors[index];
            }
            return this.departmentColors[department];
        },
        assignAgent(id) {
            log("assign agent", id);
            if (this.defaultContacts && this.defaultContacts.length > 0) {
                this.defaultContacts.forEach((contact) => {
                    if (contact.lastConversation) {
                        sendSocketMessage("user add-user-in-conversation", {
                            conversationId: contact.lastConversation._id,
                            userId: id,
                            siteId: this.site._id,
                            type: "assign",
                        });
                    }
                });
            } else if (this.activeConversation) {
                sendSocketMessage("user add-user-in-conversation", {
                    conversationId: this.activeConversation._id,
                    userId: id,
                    siteId: this.site._id,
                    type: "assign",
                });
            }
            this.$emit("close");
            this.$bvModal.hide("assign-agent");
        },
    },
};
</script>
<style lang="scss" scoped>
#JoinableAgent {
    .no-agents-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        font-size: 16px;
        color: #999;

        .anticon {
            font-size: 48px;
            margin-bottom: 16px;
        }
    }
}
</style>
